import { useCallback, useEffect } from 'react';
import { useWasQRScannedAtLocker } from '../../../../../context/Appointment/appointment';
import { useKioskTimeTable, useKioskState } from '../../../../../context/Kiosk/kiosk';
import { useLocalization } from '../../../../../context/Localization/localization';
import { useSetKioskBannerVisibility } from '../../../../../context/Page/page';
import OptionBoxes from '../../../../../controls/OptionBoxes/OptionBoxes';
import { Spinner } from '../../../../../controls/Spinner/Spinner';
import { GoMotoComponent, GoMotoFunction } from '../../../../../API/GoMoto.types';
import { AdvisorOptionProps } from './AdvisorOption.types';
import { LocalizationType } from '../../../../../context/Localization/localization.types';

/**
 * Presents the user with an option to finish their check in themselves at the outdoor kiosk or with an advisor.
 *
 * @param onSkip - {@link AdvisorOptionProps.onSkip onSkip} description.
 * @param onSeeAdvisor - {@link AdvisorOptionProps.onSeeAdvisor onSeeAdvisor} description.
 * @param onUseLocker - {@link AdvisorOptionProps.onUseLocker onUseLocker} description.
 */
export const AdvisorOption: GoMotoComponent<AdvisorOptionProps> = ({
    onSkip,
    onSeeAdvisor,
    onUseLocker,
}) => {
    const Localization: LocalizationType = useLocalization();
    const { getIsKioskActuallyClosed } = useKioskTimeTable();
    const { dealer } = useKioskState();
    const showBanner = useSetKioskBannerVisibility();
    const QRWrapScannedAtLocker: boolean = useWasQRScannedAtLocker();

    /**
     * Consolidate the redundant logic for checking if we should skip this screen into
     * a single function we can re-use.
     */
    const shouldSkipAdvisorOption: GoMotoFunction<[void], boolean> = useCallback(() => {
        return getIsKioskActuallyClosed() || QRWrapScannedAtLocker || !dealer.advisorOptionEnabled;
    }, [QRWrapScannedAtLocker, dealer.advisorOptionEnabled, getIsKioskActuallyClosed]);

    /**
     * If the advisor option is disabled or we are outside of business hours,
     * we don't provide the option to finish the check-in with an advisor.
     *
     * Also, if the QR code was scanned on the wrap of the outdoor kiosk,
     * we skip this option as well.
     */
    useEffect(() => {
        if (shouldSkipAdvisorOption()) {
            /*
             * TODO: You can spam the go back button and end up erroring out the entire appointment.
             * We should probably replace the route back since going back will skip back to the screen you tried to go back on.
             */
            onSkip();
        }
    }, [onSkip, shouldSkipAdvisorOption]);

    useEffect(() => {
        // [ ]: This seems like it should have a return to clean up setting the banner to false.
        showBanner(false);
    }, [showBanner]);

    if (shouldSkipAdvisorOption()) {
        return (
            <Spinner />
        );
    }
    else {
        return (
            <OptionBoxes
                primaryText={Localization.CheckInAtLocker}
                primaryButtonText={Localization.UseLocker}
                secondaryText={Localization.CheckInWithAdvisor}
                secondaryButtonText={Localization.SeeAdvisor}
                onPrimaryClick={onUseLocker}
                onSecondaryClick={onSeeAdvisor}
            />
        );
    }
};
